<template>
  <BaseButton :style="mobileButtonBackground" :class="[theme, {compact, shadow, 'icon-only-mobile': iconOnlyMobile, 'hide-arrow-mobile': hideArrowMobile, 'grow-hover': btnHoverGrow }]" class="mic-button" @click.native="click">
    <slot />
    <img
      v-if="hasArrow"
      src="../assets/icons/arrow-blue.svg"
      alt="arrow icon"
      class="mic-button__icon"
    >
    <img
      v-if="hasArrow"
      src="../assets/icons/arrow-white.svg"
      alt="arrow icon"
      class="mic-button__icon mic-button__icon--white"
    >
  </BaseButton>
</template>

<script>
import BaseButton from './BaseButton'

export default {
  name: 'MicButton',

  components: {
    BaseButton
  },

  props: {
    theme: {
      type: String,
      default: ''
    },
    compact: {
      type: Boolean,
      default: false
    },
    shadow: {
      type: Boolean,
      default: false
    },
    iconOnlyMobile: {
      type: Boolean,
      default: false
    },
    hasArrow: {
      type: Boolean,
      default: false
    },
    hideArrowMobile: {
      type: Boolean,
      default: false
    },
    mobileButtonIcon: {
      type: String,
      default: 'arrow-blue.svg'
    },
    btnHoverGrow: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    mobileButtonBackground () {
      const icon = this.mobileButtonIcon || 'arrow-blue.svg'
      const url = require(`../assets/icons/${icon}`)
      return { '--mobile-button-background': `url(${url})` }
    }
  },

  methods: {
    click ($event) {
      this.$emit('click', $event)
    }
  }
}
</script>

<style lang="scss" scoped>
.mic-button {
    display: flex;
    align-items: center;
    justify-content: center;
    background: $button-bg;
    border: $button-border;
    height: 56px;
    font-size: $button-font-size;
    line-height: 24px;
    color: $button-text;
    min-width: 128px;
    padding: 0 $spacing-34;
    border-radius: $button-border-radius;
    font-weight: 900;
    outline: 0 !important;
    box-shadow: $button-box-shadow;

    &.compact {
      min-width: 0;
    }

    &.shadow {
      box-shadow: $shadow-button;
    }

  &:hover{
    @media (min-width: $screen-md) {
      background: $button-bg-hover;
      color: $button-text-hover;
      box-shadow: $button-box-shadow-hover;
      border: $button-border-hover;
      position: relative;
      top: $button-top-hover;

      .mic-button__icon {
        display: none;

        &--white {
          display: inline-block;
        }
      }
    }
  }
  &.outline:hover {
    @media (min-width: $screen-md) {
      background-image: none;
      color: $button-outline-text-hover;
      background: $button-outline-bg-hover;
      top: 0;

      .mic-button__icon {
        display: none;

        &--white {
          display: inline-block;
        }
      }
    }
  }

  @media (max-width: $screen-md) {
    &:active,
    &.outline:active {
      background-image: none;
      background-color: $button-outline-bg-hover;
      color: $button-outline-text-hover;

      .mic-button__icon {
        display: none;

        &--white {
          display: inline-block;
        }
      }
    }
  }

  &.outline {
    background-image: none;
    border: $button-outline-border;
    border-radius: $button-outline-border-radius;
    background-color: $button-outline-bg;
    box-shadow: $button-outline-shadow;
    color: $button-outline-text;
    font-weight: $button-outline-font-weight;

    @media (max-width: $screen-md) {
      padding: $spacing-8 $spacing-40;
    }

    &:hover {
      @media (min-width: $screen-md) {
        border: $button-outline-border-hover !important;
        box-shadow: $button-outline-shadow-hover;
      }
    }
  }

  &.bordered-purple {
    box-shadow: unset;
    background-color: transparent;
    border-color: rgba(71, 43, 91, 0.12);
    border-width: 2px;
    font-size: 18px;
    color: $button-border-text-color;
  }

    &.bordered-purple-thick {
    box-shadow: unset;
    background-color: transparent;
    border-color: #590F85;
    border-width: 3px;
    font-size: 18px;
    color: $purple-2;
  }

  &.purple {
    box-shadow: unset;
    background-color: $purple-1;
    border: unset;
    color: white;
    min-width: 160px;
  }

  &.grow-hover {
    transition: background-color .2s linear,border-color .2s linear,box-shadow .2s linear;

    &:hover {
      color: #fff;
      text-decoration: none;
      box-shadow: 0 0 0 2px #590f85;
    }
  }

  &.teal {
    box-shadow: unset;
    background-color: $teal-1;
    border: unset;
    color: $blue-1;
    min-width: 160px;

    &:hover {
      color: $blue-1 !important;
      box-shadow: 0 0 0 2px $teal-1;
    }
  }

  &.text {
    background-color: transparent;
    background-image: none;
    border: 0;
    color: $text-color;
    text-decoration: underline;
  }

  &:disabled {
    opacity: $button-opacity-disabled;
    pointer-events: none;
  }

  &__icon {
    margin-left: $spacing-8;
    margin-top: 2px;

    &--white {
      display: none;
    }
  }

  @media (max-width: $screen-md) {
  height: 56px;
  max-width: 96px;
  min-width: 96px;
  font-size: $button-font-size;
  line-height: 20px;
  padding: $spacing-8 $spacing-20;

  &.icon-only-mobile {
    min-width: 0;
    font-size: 0;
    width: 48px;
    height: 48px;
    padding: $spacing-0;
    box-shadow: $shadow-small;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      background-image: var(--mobile-button-background);
      background-position: center;
      background-repeat: no-repeat;
    }

    .mic-button__icon {
      display: none;
    }
  }

  &.hide-arrow-mobile {
    .mic-button__icon {
      display: none;
    }
  }
 }
}
</style>
